import { forwardRef, useEffect, useRef, useState } from 'react'
import ROUTES from '@/ROUTES'
import { usePaywallModal } from '@/stripe/PayWallModal'
import { usePaywall } from '@/stripe/PayWallProvider'
import { isBefore } from 'date-fns'

import { api } from '@/services/api.service'
import { useAuth } from '@/services/authentication.service'
import { useChat } from '@/services/chat.service'
import { cn } from '@/utils/cn'

import sun from '../../../iPhone.png'
import MarkdownViewer from './MarkdownViewer'

export function Conversation({
    showQuickQuestions,
}: {
    conversationWrapperClass?: string
    showQuickQuestions: boolean
}) {
    const { chat, streamingMessage, sendMessage, chatError } = useChat()
    const { user } = useAuth()

    const [quickQuestions, setQuickQuestions] = useState<string[]>([])
    const [loadingQuestions, setLoadingQuestions] = useState(false)
    const [quickQuestionsError, setQuickQuestionsError] = useState(false)
    const latestAssistantRef = useRef(null)

    const { hasAccess } = usePaywall()

    const { showSubscriptionModal } = usePaywallModal()

    useEffect(() => {
        if (showQuickQuestions && user) {
            setLoadingQuestions(true)
            getQuickQuestions()
                .then(() => console.log(quickQuestions))
                .finally(() => setLoadingQuestions(false))
        }
    }, [showQuickQuestions, user?.userInfo])

    useEffect(() => {
        if (showQuickQuestions) return

        if (latestAssistantRef.current) {
            latestAssistantRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            })
        }
    }, [streamingMessage])

    function handleSendQuickQuestion(question: string) {
        if (!hasAccess) {
            showSubscriptionModal()
            return
        }
        sendMessage(question)
    }

    async function getQuickQuestions() {
        try {
            const res = await api<string[]>(ROUTES.app.quickQuestion)

            if (res?.status && res.status >= 400) {
                setQuickQuestionsError(true)
                setQuickQuestions([])
                return
            }

            setQuickQuestions(res.data || [])
            setQuickQuestionsError(false)
        } catch (e) {
            console.error('Error fetching quick questions for user')
            setQuickQuestionsError(true)
            setQuickQuestions([])
        }
    }
    const sortedMessages =
        chat?.messages
            ?.filter((msg) => msg.role !== 'system')
            ?.sort((a, b) => (isBefore(new Date(a?.timestamp), new Date(b?.timestamp)) ? -1 : 1)) || []

    return (
        <div className='flex flex-col mx-2 mt-3'>
            {showQuickQuestions && (
                <div className='flex flex-col items-center justify-center min-h-[60vh] py-4'>
                    <div className='w-full max-w-2xl space-y-6'>
                        <div className='mb-6'>
                            <div className='flex justify-start gap-3'>
                                <div className='flex-shrink-0'>
                                    <div className='flex items-center justify-center w-8 h-8 rounded-lg shadow-sm bg-gradient-to-br from-brand-500 to-brand-600'>
                                        <img src={sun} className='object-contain w-full h-full rounded-full' />
                                    </div>
                                </div>
                                <div className='flex flex-col max-w-[85%] sm:max-w-[75%] items-start'>
                                    <div className='px-4 py-3 bg-white border border-gray-200 shadow-sm rounded-2xl'>
                                        <div className='text-sm leading-relaxed text-gray-800'>
                                            Hi there! I'm BrightPath Care Assistant. How can I help you today?
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className='space-y-2'>
                            {quickQuestionsError ? (
                                <div className='mx-4 sm:mx-0 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3'>
                                    <p className='text-sm text-amber-800'>
                                        We're experiencing a temporary issue loading suggested questions. Our team has been notified. You can still type your own question below!
                                    </p>
                                </div>
                            ) : (
                                <>
                                    <p className='mx-5 text-sm text-gray-500'>
                                        {quickQuestions.length > 0 || loadingQuestions
                                            ? 'Choose a topic below or ask your own question'
                                            : 'Type a question below to get started'}
                                    </p>

                                    <div className='grid gap-3 mx-4 overflow-y-auto sm:mx-0'>
                                        {loadingQuestions
                                            ? Array.from({ length: 7 }).map((_, index) => (
                                                  <SimpleQuickQuestionSkeleton key={index} index={index} />
                                              ))
                                            : (quickQuestions || []).map((question, index) => (
                                                  <button
                                                      key={index}
                                                      className='w-full px-4 py-3 text-left transition-all bg-white border border-gray-100 rounded-xl hover:border-blue-300 hover:shadow-md group'
                                                      onClick={() => handleSendQuickQuestion(question)}
                                                  >
                                                      <span className='text-sm font-medium text-gray-700 '>{question}</span>
                                                  </button>
                                              ))}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <div className='space-y-4'>
                {sortedMessages.map((message, idx) => {
                    const isLastAssistant =
                        message.role === 'assistant' && idx === sortedMessages.length - 1 && !streamingMessage

                    return (
                        <Message
                            key={idx}
                            message={message}
                            user={user}
                            isStreaming={false}
                            ref={isLastAssistant ? latestAssistantRef : null}
                        />
                    )
                })}

                {streamingMessage !== null && (
                    <Message
                        message={{ role: 'assistant', content: streamingMessage, timestamp: new Date() }}
                        isStreaming={true}
                        user={user}
                        ref={latestAssistantRef}
                    />
                )}

                {chatError && (
                    <div className='flex gap-3 mb-6 justify-start'>
                        <div className='flex-shrink-0 mt-3'>
                            <div className='flex items-center justify-center w-8 h-8 rounded-lg shadow-sm bg-gradient-to-br from-amber-400 to-amber-500'>
                                <span className='text-white text-sm'>!</span>
                            </div>
                        </div>
                        <div className='flex flex-col max-w-[85%] sm:max-w-[75%] items-start'>
                            <div className='rounded-2xl px-4 py-3 shadow-sm bg-amber-50 border border-amber-200 mt-3'>
                                <p className='text-sm leading-relaxed text-amber-800'>{chatError}</p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

const Message = forwardRef(function Message(
    { message, user, isStreaming = false }: any,
    ref: React.Ref<HTMLDivElement>
) {
    return (
        <div ref={ref} className={`flex gap-3 mb-6 ${message.role === 'user' ? 'justify-end' : 'justify-start '}`}>
            {message.role === 'assistant' && (
                <div className='flex-shrink-0 mt-3'>
                    <div className='flex items-center justify-center w-8 h-8 rounded-lg shadow-sm bg-gradient-to-br from-brand-500 to-brand-600'>
                        <img src={sun} className='object-contain w-full h-full rounded-full animate-jump-in' />
                    </div>
                </div>
            )}

            <div
                className={`flex flex-col max-w-[85%] sm:max-w-[75%] ${message.role === 'user' ? 'items-end' : 'items-start'}`}
            >
                <div
                    className={`rounded-2xl px-4 py-3 shadow-sm ${
                        message.role === 'assistant'
                            ? 'bg-white border border-gray-200 mt-3'
                            : 'bg-gradient-to-br from-brand-500 to-brand-600 text-white'
                    }`}
                >
                    <div className={`${isStreaming ? 'min-h-[1.5rem]' : ''}`}>
                        <MarkdownViewer
                            markdown={message.content}
                            markdownClass={cn(
                                'text-sm sm:text-base leading-relaxed whitespace-pre-wrap',
                                message.role === 'user' ? 'text-white' : 'text-gray-800'
                            )}
                            codeWrapperClass='text-xs sm:text-sm rounded-lg my-2 bg-gray-50 border border-gray-100'
                            containerClass=''
                        />

                        {isStreaming && message.content === '' && (
                            <div className='flex items-center gap-1'>
                                <div className='w-2 h-2 bg-gray-400 rounded-full animate-pulse' />
                                <div
                                    className='w-2 h-2 bg-gray-400 rounded-full animate-pulse'
                                    style={{ animationDelay: '75ms' }}
                                />
                                <div
                                    className='w-2 h-2 bg-gray-400 rounded-full animate-pulse'
                                    style={{ animationDelay: '150ms' }}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {message.role === 'user' && (
                <div className='flex-shrink-0'>
                    <div className='flex items-center justify-center w-8 h-8 text-xs font-medium text-gray-600 bg-gray-300 rounded-lg'>
                        {user?.name?.charAt(0) || 'U'}
                    </div>
                </div>
            )}
        </div>
    )
})

function SimpleQuickQuestionSkeleton({ index }: { index: number }) {
    return (
        <div
            className='relative w-full px-4 py-3 overflow-hidden bg-white border-gray-200 rounded-xl'
            style={{ animationDelay: `${index * 150}ms` }}
        >
            <div className='absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/20 to-transparent' />

            <div className='space-y-2'>
                <div
                    className={`h-4 bg-brand-100 rounded-md ${
                        index % 3 === 0 ? 'w-4/5' : index % 3 === 1 ? 'w-3/4' : 'w-5/6'
                    }`}
                />

                {Math.random() > 0.6 && <div className='w-1/2 h-4 rounded-md bg-brand-50' />}
            </div>
        </div>
    )
}
